import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Box,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Modal,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  EditNote,
  Edit,
  Delete,
  UploadFile,
  Group,
  WarningAmber,
  Phone,
} from '@mui/icons-material';
import axios from 'axios';
import Papa from 'papaparse'; // Add this package: npm install papaparse
import { global_link, socket_base_url, socketiopath } from '../Components/Globallink';
import modalStyle from '../Components/ModalStyle';
import io from 'socket.io-client';

const Campaigns = () => {
  // State for campaigns data and pagination
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // Modals
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openDeleteAllModal, setOpenDeleteAllModal] = useState(false);
  // Form states
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processedFile, setProcessedFile] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCampaignUnique, setSelectedCampaignUnique] = useState('');
  // UI states
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('Submit');
  const [totalUsers, setTotalUsers] = useState();
  const [assignCount, setAssignCount] = useState(0);
  const [uniqueCampaigns, setUniqueCampaigns] = useState([]);
  const [assigncountValue, setAssigncountValue] = useState('');

  // International code dropdown state
  const [countryCode, setCountryCode] = useState('+254');
  const [processingStatus, setProcessingStatus] = useState('');

  // International country codes
  const countryCodes = [
    { code: '+254', country: 'Kenya' },
    { code: '+256', country: 'Uganda' },
    { code: '+255', country: 'Tanzania' },
    { code: '+234', country: 'Nigeria' },
    { code: '+250', country: 'Rwanda' },
    { code: '+1', country: 'USA/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+27', country: 'South Africa' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+86', country: 'China' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+55', country: 'Brazil' },
    { code: '+52', country: 'Mexico' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+7', country: 'Russia' },
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+20', country: 'Egypt' },
  ];

  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;

  // Function to process CSV and add country code to phone numbers
  const processCSVFile = (file, selectedCode) => {
    return new Promise((resolve, reject) => {
      setProcessingStatus('Processing phone numbers...');
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            // Get the headers
            const headers = results.meta.fields;
            
            // Find the phone number column (common names)
            const phoneColumn = headers.find(header => 
              header.toLowerCase().includes('phone') || 
              header.toLowerCase().includes('mobile') || 
              header.toLowerCase().includes('number') ||
              header.toLowerCase().includes('contact')
            );

            if (!phoneColumn) {
              reject(new Error('Could not find phone number column in CSV'));
              return;
            }

            // Process each row
            const processedData = results.data.map(row => {
              if (row[phoneColumn]) {
                let phoneNumber = row[phoneColumn].toString().trim();
                
                // Check if number already has country code
                if (!phoneNumber.startsWith('+')) {
                  // Remove any leading zeros or existing country codes
                  let cleanNumber = phoneNumber.replace(/^0+/, '');
                  cleanNumber = cleanNumber.replace(/^254/, '');
                  cleanNumber = cleanNumber.replace(/^\+/, ''); // Remove any plus sign
                  
                  // Add country code
                  row[phoneColumn] = `${selectedCode} ${cleanNumber}`;
                }
              }
              return row;
            });

            // Create new CSV string
            const newCSV = Papa.unparse(processedData, {
              quotes: false,
              delimiter: ","
            });

            // Create new file
            const newFile = new File([newCSV], file.name, {
              type: 'text/csv',
            });

            setProcessingStatus(`Processed ${processedData.length} records with country code ${selectedCode}`);
            resolve(newFile);
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  };

  // Fetch campaigns
  const fetchCampaigns = async () => {
    try {
      const authyToken = localStorage.getItem(authToken);
      const link = userLevel !== "agent" ? "/campaign/all" : "/campaign/assigned";
      const response = await api.get(link, {
        params: {
          search: searchTerm,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
        headers: {
          Authorization: `Bearer ${authyToken}`,
        },
      });
      setCampaigns(response.data.data);
      setTotalCampaigns(response.data.total);
    } catch (error) {
      console.error('Error fetching Contact:', error);
    }
  };

  // Fetch users for assignment
  const fetchUsers = async () => {
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/user/campaignusers', {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setUsers(response.data);
      setTotalUsers(response.data.length || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchUniqueCampaigns = async () => {
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/campaign/unique', {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setUniqueCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [searchTerm, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Modal handlers
  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
    setCountryCode('+254');
    setSelectedFile(null);
    setProcessedFile(null);
    setProcessingStatus('');
  };
  
  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setCampaignName('');
    setSelectedFile(null);
    setProcessedFile(null);
    setCountryCode('+254');
    setProcessingStatus('');
  };

  const handleOpenEditModal = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenEditModal(true);
    setSelectedFile(null);
    setProcessedFile(null);
    setProcessingStatus('');
  };
  
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedFile(null);
    setProcessedFile(null);
    setProcessingStatus('');
  };

  const handleOpenDeleteModal = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenDeleteModal(true);
  };
  
  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleOpenAssignModal = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenAssignModal(true);
    countAssignNumbers();
    fetchUniqueCampaigns();
  };

  const handleCloseAssignModal = () => setOpenAssignModal(false);

  const handleOpenDeleteAllModal = () => setOpenDeleteAllModal(true);
  const handleCloseDeleteAllModal = () => setOpenDeleteAllModal(false);

  // Handle file selection and processing
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setProcessingStatus('Processing file...');
    
    try {
      // Process the file with the selected country code
      const processed = await processCSVFile(file, countryCode);
      setProcessedFile(processed);
    } catch (error) {
      console.error('Error processing file:', error);
      setProcessingStatus('Error processing file: ' + error.message);
      setProcessedFile(null);
    }
  };

  // API Call Handlers
  const handleCreateCampaign = async (event) => {
    event.preventDefault();
    
    if (!processedFile || !campaignName) {
      alert("Please provide a campaign name and select a file.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", processedFile); // Use the processed file
    formData.append("campaign_name", campaignName);
    formData.append("default_country_code", countryCode);
    
    setUploadStatus('Uploading...');
    setLoading(true);
    
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.post("/campaign/add", formData, {
        headers: {
          Authorization: `Bearer ${authyToken}`,
          "Content-Type": "multipart/form-data"
        },
      });
      fetchCampaigns();
      handleCloseCreateModal();
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Error creating campaign: ' + error.message);
    } finally {
      setUploadStatus('Submit');
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.delete(`/campaign/delete/${selectedCampaign.id}`, {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      fetchCampaigns();
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllCampaigns = async () => {
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.delete(`/campaign/deleteall`, {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      fetchCampaigns();
      handleCloseDeleteAllModal();
    } catch (error) {
      console.error('Error deleting all campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = async (event) => {
    event.preventDefault();
    if (!processedFile || !selectedCampaign) {
      alert("Please select a file to upload.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", processedFile);
    formData.append("default_country_code", countryCode);
    
    setUploadStatus('Replacing contacts...');
    setLoading(true);
    
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.post(`/campaigns/${selectedCampaign.id}/contacts`, formData, {
        headers: {
          Authorization: `Bearer ${authyToken}`,
          "Content-Type": "multipart/form-data"
        },
      });
      fetchCampaigns();
      handleCloseEditModal();
    } catch (error) {
      console.error('Error editing campaign:', error);
      alert('Error editing campaign: ' + error.message);
    } finally {
      setUploadStatus('Submit');
      setLoading(false);
    }
  };

  const handleAssignCampaign = async (event) => {
    event.preventDefault();
    if (!selectedUser || !selectedCampaignUnique || !assigncountValue) {
      alert("Please select a user.");
      return;
    }
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.put(`/campaign/assign`, { 
        user_id: selectedUser, 
        campaign_id: selectedCampaignUnique, 
        assign_value: assigncountValue 
      }, {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      fetchCampaigns();
      handleCloseAssignModal();
    } catch (error) {
      console.error('Error assigning campaign:', error);
    } finally {
      setLoading(false);
    }
    setSelectedUser('');
    setSelectedCampaignUnique('');
    setAssigncountValue('');
  };

  // Utility handlers
  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/campaign_template.csv';
    link.download = 'campaign_template.csv';
    link.click();
  };

  const userLevel = sessionStorage.getItem("user_level");

  const countAssignNumbers = () => {
    setAssignCount(Math.round(totalCampaigns / totalUsers));
  };

  const copyPhoneNumber = (call_from, call_to) => {
    let call_number;
    if(call_from.length>5){
        call_number = call_from;
    }else{
        call_number = call_to;
    }
    const phoneNumber = call_number;
    const event = new CustomEvent('copyPhoneNumber', { detail: phoneNumber });
    window.dispatchEvent(event);
  }

  useEffect(() => {
    const authyToken = localStorage.getItem(authToken);
    const socket = io(socket_base_url, {
      socketiopath,
      auth: {
        token: authyToken,
      },
      transports: ['websocket'],
    });
    socket.on('connect', () => {
      console.log('Socket connected!');
    });
    socket.on('NEW_CALL_LOG_UPDATE', (message) => {
      fetchCampaigns();
      fetchUniqueCampaigns();
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <>
      <CssBaseline />
      <Typography variant='h5' sx={{ marginBottom: '15px' }}>Campaigns</Typography>
      <Box sx={{ bgcolor: 'background.paper', boxShadow: 1, borderRadius: 1 }}>
        <Box sx={{ padding: 2, display: 'flex', gap: 2 }}>
          {/* Search Field */}
          <TextField
            label="Search Campaigns"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search"
            fullWidth
          />

          {/* Add Campaign Button - visible only if not agent */}
          {userLevel !== "agent" && (
            <Button
              variant="contained"
              sx={{ width: '30%!important' }}
              color='primary'
              onClick={handleOpenCreateModal}
            >
              Add Campaign
            </Button>
          )}

          {userLevel !== "agent" && campaigns.length > 0 && (
            <>
              <Button
                variant="contained"
                sx={{ width: '30%!important' }}
                color='info'
                onClick={handleOpenAssignModal}
              >
                Assign Contacts
              </Button>
              <Button
                variant="contained"
                sx={{ width: '30%!important' }}
                color='error'
                onClick={handleOpenDeleteAllModal}
              >
                Delete All
              </Button>
            </>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.table' }}>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Contact Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                {userLevel !== "agent" && (
                  <TableCell align="center">Actions</TableCell>
                )}
              </TableRow>  
            </TableHead>
            <TableBody>
              {campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <TableRow key={campaign.id} onClick={() => copyPhoneNumber(campaign.phone_number)} sx={{cursor:'pointer'}}>
                    <TableCell>{campaign.campaign_name}</TableCell>
                    <TableCell>{campaign.phone_number}</TableCell>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>{campaign.email}</TableCell>
                    <TableCell>{campaign.label}</TableCell>
                    <TableCell>
                      <span style={{ color: campaign.assigned_name ? 'inherit' : 'red' }}>
                        {campaign.assigned_name || 'Unassigned'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: campaign.call_status === 'New' || campaign.call_status == null ? 'orange' : 'green', fontWeight:'bold' }}>
                        {campaign.call_status || 'NEW'}
                      </span>
                    </TableCell>
                    <TableCell>{campaign.date}</TableCell>
                    {userLevel !== "agent" && (
                      <TableCell align="center">
                        <Button title="Call" color="success"><Phone /></Button>
                        <Button title="Delete Campaign" color="error" onClick={() => handleOpenDeleteModal(campaign)}><Delete /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No campaigns found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCampaigns}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {/* Create Campaign Modal */}
      <Modal open={openCreateModal} onClose={handleCloseCreateModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2">Create New Campaign</Typography>
          <form onSubmit={handleCreateCampaign}>
            <TextField
              label="Campaign Name"
              name="campaign_name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              fullWidth
              required
              sx={{ mt: 2, mb: 2 }}
            />
            
            {/* International Code Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="country-code-label">Country Code</InputLabel>
              <Select
                labelId="country-code-label"
                value={countryCode}
                label="Country Code"
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  // If a file is already selected, reprocess it with new country code
                  if (selectedFile) {
                    processCSVFile(selectedFile, e.target.value)
                      .then(setProcessedFile)
                      .catch(console.error);
                  }
                }}
              >
                {countryCodes.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button component="label" color="info" variant="outlined" fullWidth sx={{ marginLeft: 'auto', padding: '10px' }}>
              Upload Contacts CSV
              <input 
                type="file" 
                accept=".csv" 
                hidden 
                onChange={handleFileChange}
              />
            </Button>
            
            {selectedFile && (
              <>
                <Typography sx={{ mt: 1 }}>Original file: {selectedFile.name}</Typography>
                {processingStatus && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {processingStatus}
                  </Alert>
                )}
              </>
            )}
            
            <Button onClick={downloadTemplate} sx={{ mt: 1 }} color="success" variant="contained">
              Download Template
            </Button>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseCreateModal}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || !processedFile} 
                sx={{ ml: 1 }}
              >
                {loading ? 'Creating...' : 'Create'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Edit Campaign Modal */}
      <Modal open={openEditModal} onClose={handleCloseEditModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6">Edit Campaign: {selectedCampaign?.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            This will delete all existing contacts in the campaign and replace them with contacts from the new file.
          </Typography>
          <form onSubmit={handleEditCampaign}>
            {/* International Code Dropdown for Edit */}
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="edit-country-code-label">Country Code</InputLabel>
              <Select
                labelId="edit-country-code-label"
                value={countryCode}
                label="Country Code"
                onChange={(e) => {
                  setCountryCode(e.target.value);
                  // If a file is already selected, reprocess it with new country code
                  if (selectedFile) {
                    processCSVFile(selectedFile, e.target.value)
                      .then(setProcessedFile)
                      .catch(console.error);
                  }
                }}
              >
                {countryCodes.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.code} - {item.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button component="label" variant="outlined" fullWidth sx={{ mt: 2 }}>
              <UploadFile sx={{ mr: 1 }} />
              Upload New Contacts CSV
              <input 
                type="file" 
                accept=".csv" 
                hidden 
                onChange={handleFileChange}
              />
            </Button>
            
            {selectedFile && (
              <>
                <Typography sx={{ mt: 1 }}>Original file: {selectedFile.name}</Typography>
                {processingStatus && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {processingStatus}
                  </Alert>
                )}
              </>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseEditModal}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={loading || !processedFile} 
                sx={{ ml: 1 }}
              >
                {loading ? 'Uploading...' : 'Replace Contacts'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Delete Campaign Modal */}
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" textAlign="center">
            <WarningAmber sx={{ fontSize: '4rem', color: 'red' }} />
            <br />
            Delete Campaign Contact
          </Typography>
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Are you sure you want to delete the campaign contact "<strong>{selectedCampaign?.name}</strong>"?
            This action is irreversible and will delete all associated contacts.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={handleCloseDeleteModal}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteCampaign} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete All Campaigns Modal */}
      <Modal open={openDeleteAllModal} onClose={handleCloseDeleteAllModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" textAlign="center">
            <WarningAmber sx={{ fontSize: '4rem', color: 'red' }} />
            <br />
            Delete All Campaigns
          </Typography>
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Are you sure you want to delete all campaigns? This action is irreversible and will delete all associated contacts.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={handleCloseDeleteAllModal}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteAllCampaigns} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete All'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Assign Campaign Modal */}
      <Modal open={openAssignModal} onClose={handleCloseAssignModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6">Assign Campaign</Typography>
          <form onSubmit={handleAssignCampaign}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="assign-user-label">Select User</InputLabel>
              <Select
                labelId="assign-user-label"
                value={selectedUser}
                label="Select User"
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                {users.map((user) => (
                  <MenuItem key={user.user_id} value={user.user_id}>
                    {user.user_name} ({user.user_extension})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="assign-campaign-label">Select Campaign</InputLabel>
              <Select
                labelId="assign-campaign-label"
                value={selectedCampaignUnique}
                label="Select Campaign"
                onChange={(e) => setSelectedCampaignUnique(e.target.value)}
              >
                {uniqueCampaigns.map((campaign) => (
                  <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                    {campaign.campaign_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Assign Count"
              name="assign_count"
              value={assigncountValue}
              onChange={(e) => setAssigncountValue(e.target.value)}
              placeholder={assignCount}
              fullWidth
              required
              sx={{ mt: 2, mb: 2 }}
            />

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseAssignModal}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading} sx={{ ml: 1 }}>
                {loading ? 'Assigning...' : 'Assign'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default Campaigns;